import { Bell, BellSlash, CheckCircle, WarningCircle } from '@phosphor-icons/react'
import { useId } from 'react'
import { useTranslation } from 'react-i18next'

import type { NativePushState } from '../hooks/use-native-push'
import { NotificationNotice } from './notice'

export function NativePushCard({ state }: { state: NativePushState }) {
  const { t } = useTranslation()
  const headingId = useId()
  if (!state.supported) return null

  const enabling = state.busy === 'enabling'
  const disabling = state.busy === 'disabling'

  return (
    <section
      aria-labelledby={headingId}
      className="mt-5 rounded-card border border-line bg-white p-4 md:p-5"
    >
      <h2 id={headingId} className="text-xl">
        {t('notifications.nativePush.title')}
      </h2>
      <p className="mt-1 max-w-[60ch] text-sm leading-relaxed text-muted">
        {t('notifications.nativePush.body')}
      </p>

      {state.permission === 'denied' ? (
        <div
          role="alert"
          className="mt-3 flex items-start gap-3 rounded-md border border-[var(--danger)] bg-[var(--danger-soft)] px-4 py-3 text-danger"
        >
          <WarningCircle size={20} weight="bold" aria-hidden="true" className="mt-0.5 shrink-0" />
          <div>
            <p className="font-bold">{t('notifications.nativePush.deniedTitle')}</p>
            <p className="mt-1 text-sm leading-relaxed">
              {t('notifications.nativePush.deniedBody')}
            </p>
          </div>
        </div>
      ) : (
        <>
          <p className="mt-3 flex items-center gap-2 text-sm font-bold text-teal">
            {state.subscribed ? (
              <CheckCircle size={18} weight="bold" aria-hidden="true" />
            ) : (
              <BellSlash size={18} weight="bold" aria-hidden="true" />
            )}
            {state.subscribed
              ? t('notifications.nativePush.enabled')
              : t('notifications.nativePush.inactive')}
          </p>

          {!state.subscribed ? (
            <div className="mt-3">
              <button
                type="button"
                className="btn-primary min-h-11 px-5 text-sm"
                onClick={() => void state.enable()}
                disabled={enabling}
                aria-busy={enabling}
              >
                <Bell size={18} weight="bold" aria-hidden="true" />
                {enabling
                  ? t('notifications.nativePush.enabling')
                  : t('notifications.nativePush.enable')}
              </button>
              <p className="mt-2 text-xs leading-relaxed text-muted">
                {t('notifications.nativePush.permissionHint')}
              </p>
            </div>
          ) : (
            <button
              type="button"
              className="btn-secondary mt-3 min-h-11 px-5 text-sm"
              onClick={() => void state.disable()}
              disabled={disabling}
              aria-busy={disabling}
            >
              <BellSlash size={18} weight="bold" aria-hidden="true" />
              {disabling
                ? t('notifications.nativePush.disabling')
                : t('notifications.nativePush.disable')}
            </button>
          )}
        </>
      )}

      {state.status ? (
        <div className="mt-3">
          <NotificationNotice
            tone={state.status.tone}
            messageKey={state.status.messageKey}
            onDismiss={state.dismissStatus}
          />
        </div>
      ) : null}
    </section>
  )
}
